import React, { useCallback, useState } from "react";
import useApiEp from "../../hooks/useApiEp";
import { API_ENDPOINT } from "../../utils/constants";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  exportToExcel,
  getErrorResponse,
  getHeader,
  roundToMaxDecimals,
  toastError,
} from "../../utils/util";
import { Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import CustomCircularP from "../../widgets/CustomCircularP";
import { toast } from "react-toastify";
import axios from "axios";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import PageTitle from "../../widgets/PageTitle";
import SearchIcon from "@mui/icons-material/Search";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { debounce } from "lodash";
import DataTable from "../../widgets/DataTable";
import TitleActionsContainer from "../../widgets/TitleActionsContainer";
import { GlassButtonLoading } from "../../themes/UiElements";
const ResellersList = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [showDelete, setShowDelete] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toDelete, setToDelete] = useState("");
  const [partner, setPartner] = useState("");
  const [name, setName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [sq, setSq] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSq(value);
    }, 500),
    []
  );
  const handleSearchChange = (value) => {
    setName(value);
    debouncedSearch(value);
  };
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/user/get-resellers?page=${page}&limit=${limit}&name=${sq}&partner=${partner}`
  );
  const handleAction = useCallback((action, id) => {
    switch (action) {
      case "view":
        window.open(`/home/${id}/reseller`, "_blank"); // Open in new tab
        break;
      case "edit":
        window.open(`/home/${id}/edit-reseller`, "_blank"); // Open in new tab
        break;
      case "delete":
        setToDelete(id);
        setShowDelete(true);
        break;
      default:
        console.warn("unknown action", id);
    }
  }, []);

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Container sx={{ mt: 3 }}>
      <TitleActionsContainer>
        <PageTitle>Resellers List ({data?.totalItems || 0})</PageTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 1, sm: 0 },
          }}
        >
          <TextField
            size="small"
            value={name}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search Accounts"
            InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
          />
          <GlassButtonLoading
            onClick={() => {
              navigate(`/home/add-reseller`);
            }}
            variant="outlined"
            startIcon={<AddOutlinedIcon />}
          >
            Add Reseller
          </GlassButtonLoading>
          <GlassButtonLoading
            loading={exporting}
            onClick={async () => {
              try {
                setExporting(true);
                const response = await axios.get(
                  `${API_ENDPOINT}/root/get-all-accounts`,
                  getHeader()
                );
                exportToExcel(response.data, "accounts_list.xlsx");
              } catch (error) {
                toastError(error);
              } finally {
                setExporting(false);
              }
            }}
            variant="contained"
            startIcon={<ImportExportIcon />}
          >
            Export All
          </GlassButtonLoading>
        </Stack>
      </TitleActionsContainer>

      <CustomCircularP show={deleting || isLoading} />
      <SimpleYesNoDialog
        show={showDelete}
        setShow={setShowDelete}
        title={"Please confirm!"}
        message={"Are you sure you want to delete this item?"}
        onYes={async () => {
          try {
            setDeleting(true);
            setShowDelete(false);
            const response = await axios.delete(
              `${API_ENDPOINT}/root/delete-account/${toDelete}`,
              getHeader()
            );
            refetch();
            toast.success(response.data.message);
          } catch (error) {
            toastError(error);
          } finally {
            setDeleting(false);
          }
        }}
      />

      <DataTable
        sx={{ mb: 1 }}
        columns={[
          {
            id: "serial",
            label: "S.No.",
            render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
          },
          {
            id: "username",
            label: "Username",
          },
          {
            id: "name",
            label: "Name",
            render: (v, row) =>
              `${row.firstName || ""} ${row.lastName || ""}`.trim() || "NA",
          },
          {
            id: "companyName",
            label: "Company Name",
          },
          {
            id: "status",
            label: "Status",
            render: (v, row) =>
              row.isActive ? (
                <Chip
                  label="Active"
                  variant="outlined"
                  size="small"
                  color="success"
                />
              ) : (
                <Chip
                  label="Inactive"
                  variant="outlined"
                  size="small"
                  color="error"
                />
              ),
          },
          {
            id: "createdOn",
            label: "Created On",
            render: (v) =>
              v
                ? new Date(v).toLocaleString("en-IN", {
                    dateStyle: "medium",
                  })
                : "NA",
          },
        ]}
        actions={["view", "edit", "delete"]}
        rows={data?.data ?? []}
        page={page}
        itemsPerPage={limit}
        totalPages={data?.totalPages ?? 1}
        onPageChange={setPage}
        onAction={handleAction}
        itemsPerPageOptions={[20, 30, 50, 100]}
        onItemsPerPageChange={(lm) => {
          setLimit(lm);
        }}
        paginationAlign="end"
      />
    </Container>
  );
};

export default ResellersList;
